import React, { Fragment, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import { Toast } from 'primereact/toast'
import { API_URL } from '../../../utils/Constants/URLS'
import Modal from 'react-bootstrap/Modal'
import { useNavigate } from 'react-router-dom'

const FocEnquiryDetails = forwardRef((props, ref) => {
    const navigate = useNavigate()
    const { id } = props
    const toast = useRef(null)
    const token = localStorage.getItem('token')
    const [modalMessage, setModalMessage] = useState('')
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showDeclineModal, setShowDeclineModal] = useState(false)
    const [focLeadDetails, setFocLeadDetails] = useState(null)
    const [isCompleted, setIsCompleted] = useState(false)
    const [data, setData] = useState({
        enquiryDetails: {
            projectType: '',
            contactEstablished: null,
            informationReceived: null,
            furtherInformation: '',
            userIdentified: null,
            anyPastMeeting: null,
            meetingDetails: [
                { date: '', notes: '' }
            ],
            tentativeClosureDate: '',
            needIdentified: null,
            terminateLead: null,
            status: 'done'
        }
    })

    // Expose saveData method for Save All functionality
    useImperativeHandle(ref, () => ({
        saveData: async () => {
            // Skip if already completed
            if (isCompleted) {
                return { success: true, skipped: true, stage: 'enquiryDetails' }
            }

            // Save current form data with pending status (no validation required for Save All)
            const enquiry = {
                ...data.enquiryDetails,
                status: 'done'
            }

            try {
                const res = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        enquiryDetails: enquiry
                    })
                })
                const result = await res.json()
                return { success: result.error === false, stage: 'enquiryDetails' }
            } catch (error) {
                console.error('Error saving enquiry details:', error)
                return { success: false, stage: 'enquiryDetails', error }
            }
        },
        isCompleted: () => isCompleted
    }))

    // Sync with parent focLeadDetails prop
    useEffect(() => {
        if (props.focLeadDetails) {
            const leadData = props.focLeadDetails
            setFocLeadDetails(leadData)

            // Check if this stage is completed
            if (leadData?.enquiryDetails?.status === 'done') {
                setIsCompleted(true)
            }

            // Prefill form logic
            // Priority: FOC lead's enquiryDetails > originalLeadId's enquiryDetails
            const ed = leadData?.enquiryDetails || leadData?.originalLeadId?.enquiryDetails

            if (ed) {
                setData(prev => ({
                    enquiryDetails: {
                        projectType: ed.projectType || '',
                        contactEstablished: ed.contactEstablished ?? null,
                        informationReceived: ed.informationReceived ?? null,
                        furtherInformation: ed.furtherInformation || '',
                        userIdentified: ed.userIdentified ?? null,
                        anyPastMeeting: ed.anyPastMeeting ?? null,
                        meetingDetails: ed.meetingDetails?.length
                            ? ed.meetingDetails.map(m => ({
                                date: m.date
                                    ? new Date(m.date).toISOString().split('T')[0]
                                    : '',
                                notes: m.notes || ''
                            }))
                            : [{ date: '', notes: '' }],
                        tentativeClosureDate: ed.tentativeClosureDate
                            ? new Date(ed.tentativeClosureDate).toISOString().split('T')[0]
                            : '',
                        needIdentified: ed.needIdentified ?? null,
                        terminateLead: ed.terminateLead ?? null,
                        status: ed.status || 'done'
                    }
                }))
            }
        }
    }, [props.focLeadDetails])

    // Fetch FOC Lead Details
    useEffect(() => {
        const fetchFocLead = async () => {
            try {
                const res = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const response = await res.json()
                const leadData = response.data || response
                setFocLeadDetails(leadData)

                // The prefill logic is now handled by the useEffect watching props.focLeadDetails
                // This useEffect only fetches if props.focLeadDetails is not provided.
                // So, if it fetches, it should also handle prefilling.
                // However, to avoid duplication and ensure single source of truth for prefill,
                // we'll keep the prefill logic in the props.focLeadDetails useEffect.
                // This useEffect will only set focLeadDetails state if it's the primary fetcher.
                // The prefill logic will then be triggered by the props.focLeadDetails useEffect
                // when focLeadDetails state updates (if props.focLeadDetails is not initially present).
                // To simplify, if props.focLeadDetails is NOT present, this fetcher will set the state,
                // and the other useEffect will pick it up.
                // If props.focLeadDetails IS present, this fetcher won't run.

                // Check if this stage is completed (only if props.focLeadDetails wasn't the source)
                if (!props.focLeadDetails && leadData?.enquiryDetails?.status === 'done') {
                    setIsCompleted(true)
                }

                // Prefill form: use FOC lead data if exists, otherwise use original lead data
                // This block is removed from here as it's now handled by the new useEffect watching props.focLeadDetails
                // The new useEffect will be triggered either by props.focLeadDetails directly,
                // or by the focLeadDetails state update from this fetcher.
                const ed = leadData?.enquiryDetails || leadData?.originalLeadId?.enquiryDetails

                if (ed && !props.focLeadDetails) { // Only prefill if not coming from props
                    setData(prev => ({
                        enquiryDetails: {
                            projectType: ed.projectType || '',
                            contactEstablished: ed.contactEstablished ?? null,
                            informationReceived: ed.informationReceived ?? null,
                            furtherInformation: ed.furtherInformation || '',
                            userIdentified: ed.userIdentified ?? null,
                            anyPastMeeting: ed.anyPastMeeting ?? null,
                            meetingDetails: ed.meetingDetails?.length
                                ? ed.meetingDetails.map(m => ({
                                    date: m.date
                                        ? new Date(m.date).toISOString().split('T')[0]
                                        : '',
                                    notes: m.notes || ''
                                }))
                                : [{ date: '', notes: '' }],
                            tentativeClosureDate: ed.tentativeClosureDate
                                ? new Date(ed.tentativeClosureDate).toISOString().split('T')[0]
                                : '',
                            needIdentified: ed.needIdentified ?? null,
                            terminateLead: ed.terminateLead ?? null,
                            status: ed.status || 'done'
                        }
                    }))
                }
            } catch (e) {
                console.error('Error fetching FOC lead:', e)
            }
        }

        if (id && !props.focLeadDetails) {
            fetchFocLead()
        }
    }, [id, token, props.focLeadDetails])

    // Handle meeting details change
    const handleMeetingDetailsChange = (index, event) => {
        const { name, value } = event.target
        setData(prevData => {
            const updatedMeetings = [...prevData.enquiryDetails.meetingDetails]
            updatedMeetings[index][name] = value
            return {
                ...prevData,
                enquiryDetails: {
                    ...prevData.enquiryDetails,
                    meetingDetails: updatedMeetings
                }
            }
        })
    }

    // Add new meeting row
    const handleAddMeetingRow = () => {
        setData(prevData => ({
            ...prevData,
            enquiryDetails: {
                ...prevData.enquiryDetails,
                meetingDetails: [
                    ...prevData.enquiryDetails.meetingDetails,
                    { date: '', notes: '' }
                ]
            }
        }))
    }

    // Remove meeting row
    const handleRemoveMeetingRow = index => {
        setData(prevData => {
            const updatedMeetings = [...prevData.enquiryDetails.meetingDetails]
            updatedMeetings.splice(index, 1)
            return {
                ...prevData,
                enquiryDetails: {
                    ...prevData.enquiryDetails,
                    meetingDetails: updatedMeetings
                }
            }
        })
    }

    const handleCloseModal = () => {
        setShowSuccessModal(false)
        setShowDeclineModal(false)
        window.location.reload() // Refresh to show updated data
    }

    // Handle input changes for text inputs
    const handleInputChange = event => {
        const { name, value, dataset } = event.target
        setData(prevData => ({
            ...prevData,
            [dataset.section]: {
                ...prevData[dataset.section],
                [name]: value
            }
        }))
    }

    const handleRadioChange = event => {
        const { name, value, dataset } = event.target
        setData(prevData => ({
            ...prevData,
            [dataset.section]: {
                ...prevData[dataset.section],
                [name]: value === 'true'
            }
        }))
    }

    const saveUpdateEnquiryDetails = async e => {
        e.preventDefault()
        const enquiry = {
            ...data.enquiryDetails,
            status: 'pending'
        }

        try {
            const res = await fetch(`${API_URL}foc/lead/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    enquiryDetails: enquiry
                })
            })
            const result = await res.json()
            if (result.error === false) {
                setModalMessage('Enquiry Details saved successfully')
                setShowSuccessModal(true)
            } else {
                setModalMessage('Something Went Wrong')
                setShowDeclineModal(true)
            }
        } catch (e) {
            setModalMessage('Oops! Unable to add data')
            setShowDeclineModal(true)
        }
    }

    const handleUpdateEnquiryDetails = async e => {
        e.preventDefault()
        const enquiry = data.enquiryDetails

        // Check if at least one line item exists
        const lineItems = focLeadDetails?.productRequirements?.crossSells || []
        const activeLineItems = lineItems.filter(item => item.status !== 'deleted')

        if (activeLineItems.length === 0) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please add at least one line item before submitting Enquiry Details',
                life: 4000
            })
            return
        }

        // Validation logic
        if (
            focLeadDetails?.productRequirements?.natureOfSale === 'project' &&
            enquiry.projectType === ''
        ) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select The Project Type',
                life: 3000
            })
            return
        }

        if (enquiry.contactEstablished === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Contact Established Or Not',
                life: 3000
            })
            return
        }

        if (enquiry.contactEstablished === false) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Lead cannot be proceed since contact is not established',
                life: 3000
            })
            return
        }

        if (enquiry.informationReceived === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Information Received or Not',
                life: 3000
            })
            return
        }

        if (enquiry.informationReceived === false) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Lead cannot be proceed since information is not received',
                life: 3000
            })
            return
        }

        if (enquiry.userIdentified === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select User Identified or Not',
                life: 3000
            })
            return
        }

        if (enquiry.userIdentified === false) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Lead cannot be proceed since user is not identified',
                life: 3000
            })
            return
        }

        if (enquiry.anyPastMeeting === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Past Meeting Done or Not',
                life: 3000
            })
            return
        }

        if (enquiry.anyPastMeeting === true) {
            const emptyDateMeeting = enquiry.meetingDetails.find(
                meeting => meeting.date === ''
            )
            if (emptyDateMeeting) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please Enter all Meeting Dates',
                    life: 3000
                })
                return
            }

            const emptyNotesMeeting = enquiry.meetingDetails.find(
                meeting => meeting.notes === ''
            )
            if (emptyNotesMeeting) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please Enter all Meeting Notes',
                    life: 3000
                })
                return
            }
        }

        if (enquiry.tentativeClosureDate === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter Tentative Closure Date',
                life: 3000
            })
            return
        }

        if (enquiry.needIdentified === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Need Identified or Not',
                life: 3000
            })
            return
        }

        if (enquiry.needIdentified === false) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Lead cannot be proceed since need is not identified',
                life: 3000
            })
            return
        }

        const finalEnquiry = {
            ...enquiry,
            status: 'done'
        }

        try {
            const res = await fetch(`${API_URL}foc/lead/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    enquiryDetails: finalEnquiry
                })
            })
            const result = await res.json()
            if (result.error === false) {
                setModalMessage('Enquiry Details updated successfully')
                setShowSuccessModal(true)
            } else {
                setModalMessage('Something Went Wrong')
                setShowDeclineModal(true)
            }
        } catch (e) {
            setModalMessage('Oops! Unable to add data')
            setShowDeclineModal(true)
        }
    }

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '-'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        } catch {
            return dateString
        }
    }

    return (
        <Fragment>
            <div>
                {isCompleted ? (
                    // Completed View - Read Only Display
                    <div className='completed-stage-view'>
                        <div className='row g-3'>
                            {focLeadDetails?.productRequirements?.natureOfSale === 'project' && (
                                <div className='col-md-4'>
                                    <div className='mb-3'>
                                        <label className='form-label text-muted fw-semibold'>Project Type</label>
                                        <p className='mb-0 fs-6'>{data.enquiryDetails.projectType || '-'}</p>
                                    </div>
                                </div>
                            )}
                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Contact Established</label>
                                    <p className='mb-0 fs-6'>{data.enquiryDetails.contactEstablished === true ? 'Yes' : data.enquiryDetails.contactEstablished === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-4'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Information Received</label>
                                    <p className='mb-0 fs-6'>{data.enquiryDetails.informationReceived === true ? 'Yes' : data.enquiryDetails.informationReceived === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Further Info to be Added</label>
                                    <p className='mb-0 fs-6'>{data.enquiryDetails.furtherInformation || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-3'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Identified User</label>
                                    <p className='mb-0 fs-6'>{data.enquiryDetails.userIdentified === true ? 'Yes' : data.enquiryDetails.userIdentified === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-3'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Any Past Meeting?</label>
                                    <p className='mb-0 fs-6'>{data.enquiryDetails.anyPastMeeting === true ? 'Yes' : data.enquiryDetails.anyPastMeeting === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            {data.enquiryDetails.anyPastMeeting === true && data.enquiryDetails.meetingDetails?.length > 0 && (
                                <div className='col-md-12'>
                                    <div className='mb-3'>
                                        <label className='form-label text-muted fw-semibold'>Meeting Details</label>
                                        {data.enquiryDetails.meetingDetails.map((meeting, index) => (
                                            <div key={index} className='border rounded p-2 mb-2 bg-light'>
                                                <p className='mb-1'><strong>Meeting {index + 1}:</strong></p>
                                                <p className='mb-0 small'>Date: {formatDate(meeting.date)} | Notes: {meeting.notes || '-'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Tentative Closure Date</label>
                                    <p className='mb-0 fs-6'>{formatDate(data.enquiryDetails.tentativeClosureDate)}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Need Identified</label>
                                    <p className='mb-0 fs-6'>{data.enquiryDetails.needIdentified === true ? 'Yes' : data.enquiryDetails.needIdentified === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                        </div>
                        <div className='d-flex align-items-center mt-2'>
                            <span className='badge bg-success'>
                                <i className='bi bi-check-circle me-1'></i> Completed
                            </span>
                        </div>
                    </div>
                ) : (
                    // Form View - Editable
                    <form className='row g-3 needs-validation' noValidate>
                        {focLeadDetails?.productRequirements?.natureOfSale === 'project' ? (
                            <div className='col-md-4'>
                                <label for='name' className='form-label'>
                                    Project Type <span className='text-danger'>*</span>
                                </label>
                                <select
                                    data-section='enquiryDetails'
                                    name='projectType'
                                    value={data.enquiryDetails.projectType}
                                    onChange={handleInputChange}
                                    className='form-select'
                                >
                                    <option value=''>Select</option>
                                    <option value='New'>New</option>
                                    <option value='Expansion'>Expansion</option>
                                    <option value='Modernization'>Modernization</option>
                                </select>
                            </div>
                        ) : (
                            ''
                        )}

                        <div className='col-md-4'>
                            <label for='name' className='form-label'>
                                Contact Established <span className='text-danger'>*</span>
                            </label>
                            <br />
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='contactEstablished'
                                    value={true}
                                    checked={data.enquiryDetails.contactEstablished === true}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='inlineCheckbox1'>
                                    Yes
                                </label>
                            </div>
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='contactEstablished'
                                    value={false}
                                    checked={data.enquiryDetails.contactEstablished === false}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='inlineCheckbox1'>
                                    No
                                </label>
                            </div>
                        </div>
                        <div className='col-md-4'>
                            <label for='name' className='form-label'>
                                Information Received <span className='text-danger'>*</span>
                            </label>
                            <br />
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='informationReceived'
                                    value={true}
                                    checked={data.enquiryDetails.informationReceived === true}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='info-received'>
                                    Yes
                                </label>
                            </div>
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='informationReceived'
                                    value={false}
                                    checked={data.enquiryDetails.informationReceived === false}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='info-received'>
                                    No
                                </label>
                            </div>
                        </div>
                        <div className='col-md-6'>
                            <label for='further-info' className='form-label'>
                                Further Info to be added?
                            </label>
                            <input
                                name='furtherInformation'
                                value={data.enquiryDetails.furtherInformation}
                                onChange={handleInputChange}
                                type='text'
                                data-section='enquiryDetails'
                                className='form-control'
                                id='enquiry-details'
                            />
                        </div>
                        <div className='col-md-3'>
                            <label for='name' className='form-label'>
                                Identified User <span className='text-danger'>*</span>
                            </label>
                            <br />
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='userIdentified'
                                    value={true}
                                    checked={data.enquiryDetails.userIdentified === true}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='inlineCheckbox1'>
                                    Yes
                                </label>
                            </div>
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='userIdentified'
                                    value={false}
                                    checked={data.enquiryDetails.userIdentified === false}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='inlineCheckbox1'>
                                    No
                                </label>
                            </div>
                        </div>
                        <div className='col-md-3'>
                            <label for='name' className='form-label'>
                                Any Past Meeting? <span className='text-danger'>*</span>
                            </label>
                            <br />
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='anyPastMeeting'
                                    value={true}
                                    checked={data.enquiryDetails.anyPastMeeting === true}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='inlineCheckbox1'>
                                    Yes
                                </label>
                            </div>
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='anyPastMeeting'
                                    value={false}
                                    checked={data.enquiryDetails.anyPastMeeting === false}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='inlineCheckbox1'>
                                    No
                                </label>
                            </div>
                        </div>
                        {data.enquiryDetails.anyPastMeeting === true && (
                            <Fragment>
                                {data.enquiryDetails.meetingDetails.map((meeting, index) => (
                                    <div key={index} className='row align-items-center mb-3'>
                                        <h5>Meeting Details {index + 1}</h5>
                                        <div className='col-md-5'>
                                            <label
                                                htmlFor={`meetingDate-${index}`}
                                                className='form-label'
                                            >
                                                Date and time of meeting{' '}
                                                <span className='text-danger'>*</span>
                                            </label>
                                            <input
                                                type='date'
                                                name='date'
                                                value={meeting.date}
                                                onChange={e => handleMeetingDetailsChange(index, e)}
                                                className='form-control'
                                                onClick={e =>
                                                    e.target.showPicker && e.target.showPicker()
                                                }
                                            />
                                        </div>

                                        <div className='col-md-5'>
                                            <label
                                                htmlFor={`meetingNotes-${index}`}
                                                className='form-label'
                                            >
                                                Meeting Notes <span className='text-danger'>*</span>
                                            </label>
                                            <textarea
                                                name='notes'
                                                value={meeting.notes}
                                                onChange={e => handleMeetingDetailsChange(index, e)}
                                                className='form-control'
                                            />
                                        </div>

                                        <div className='col-md-2 d-flex align-items-center gap-2 mt-4'>
                                            <button
                                                type='button'
                                                className='btn btn-success'
                                                onClick={handleAddMeetingRow}
                                            >
                                                +
                                            </button>
                                            {index >= 1 && (
                                                <button
                                                    type='button'
                                                    className='btn btn-danger'
                                                    onClick={() => handleRemoveMeetingRow(index)}
                                                >
                                                    -
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </Fragment>
                        )}

                        <div className='col-md-6'>
                            <label for='name' className='form-label'>
                                Tentative Closure Date <span className='text-danger'>*</span>
                            </label>
                            <input
                                name='tentativeClosureDate'
                                value={data.enquiryDetails.tentativeClosureDate}
                                onChange={handleInputChange}
                                onClick={e => e.target.showPicker && e.target.showPicker()}
                                className='form-control'
                                min='2024-12-13'
                                max='2099-12-31'
                                type='date'
                                id='Test_DatetimeLocal'
                                data-section='enquiryDetails'
                            />
                        </div>

                        <div className='col-md-6'>
                            <label for='name' className='form-label'>
                                Need Identified <span className='text-danger'>*</span>
                            </label>
                            <br />
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='needIdentified'
                                    value={true}
                                    checked={data.enquiryDetails.needIdentified === true}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='inlineCheckbox1'>
                                    Yes
                                </label>
                            </div>
                            <div className='form-check form-check-inline'>
                                <input
                                    type='radio'
                                    name='needIdentified'
                                    value={false}
                                    checked={data.enquiryDetails.needIdentified === false}
                                    onChange={handleRadioChange}
                                    data-section='enquiryDetails'
                                />
                                <label className='form-check-label' for='flexRadioDefault1'>
                                    No
                                </label>
                            </div>
                        </div>

                        <div className='col-md-12 d-flex justify-content-end gap-2'>
                            <button
                                onClick={saveUpdateEnquiryDetails}
                                className='btn btn-secondary text-white'
                                type='button'
                            >
                                Save
                            </button>
                            <button
                                onClick={handleUpdateEnquiryDetails}
                                className='btn add-btn text-white'
                                type='button'
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                )}
                <Modal show={showSuccessModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 shrink-0 fill-green-500 inline'
                                viewBox='0 0 512 512'
                            >
                                <path
                                    d='M383.841 171.838c-7.881-8.31-21.02-8.676-29.343-.775L221.987 296.732l-63.204-64.893c-8.005-8.213-21.13-8.393-29.35-.387-8.213 7.998-8.386 21.137-.388 29.35l77.492 79.561a20.687 20.687 0 0 0 14.869 6.275 20.744 20.744 0 0 0 14.288-5.694l147.373-139.762c8.316-7.888 8.668-21.027.774-29.344z'
                                    data-original='#000000'
                                />
                                <path
                                    d='M256 0C114.84 0 0 114.84 0 256s114.84 256 256 256 256-114.84 256-256S397.16 0 256 0zm0 470.487c-118.265 0-214.487-96.214-214.487-214.487 0-118.265 96.221-214.487 214.487-214.487 118.272 0 214.487 96.221 214.487 214.487 0 118.272-96.215 214.487-214.487 214.487z'
                                    data-original='#000000'
                                />
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                                Good Job
                            </h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                                {modalMessage}!
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-green-500 hover:bg-green-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>
                <Modal show={showDeclineModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 fill-red-500 inline'
                                viewBox='0 0 286.054 286.054'
                            >
                                <path
                                    fill='#e2574c'
                                    d='M143.027 0C64.04 0 0 64.04 0 143.027c0 78.996 64.04 143.027 143.027 143.027 78.996 0 143.027-64.022 143.027-143.027C286.054 64.04 222.022 0 143.027 0zm0 259.236c-64.183 0-116.209-52.026-116.209-116.209S78.844 26.818 143.027 26.818s116.209 52.026 116.209 116.209-52.026 116.209-116.209 116.209zm.009-196.51c-10.244 0-17.995 5.346-17.995 13.981v79.201c0 8.644 7.75 13.972 17.995 13.972 9.994 0 17.995-5.551 17.995-13.972V76.707c-.001-8.43-8.001-13.981-17.995-13.981zm0 124.997c-9.842 0-17.852 8.01-17.852 17.86 0 9.833 8.01 17.843 17.852 17.843s17.843-8.01 17.843-17.843c-.001-9.851-8.001-17.86-17.843-17.86z'
                                    data-original='#e2574c'
                                ></path>
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                                Error Occured
                            </h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                                {modalMessage}!
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-red-500 hover:bg-red-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>
                <Toast ref={toast} />
            </div>
        </Fragment>
    )
})

export default FocEnquiryDetails
